import React, { useEffect, useState } from "react";
import styles from "./external-shipment-page.module.css";
import { useLocation, useParams } from "wouter";
import { FbaShipment, FbaShipmentFromAddress } from "@/contexts/ShipmentContext";
import { getBillOfLadingV2, getShipmentV2 } from "@/api/fulfillment";
import { useInventoryContext } from "@/contexts/InventoryContext";
import Button from "@/components/forms/Button/Button";
import { IconArrowDown, IconArrowLeft } from "@/components/icons/IconArrow";
import IconDownloadBox from "@/components/icons/IconDownloadBox";
import IconInfoFilled from "@/components/icons/IconInfoFilled";
import IconClock from "@/components/icons/IconClock";
import IconCalendar from "@/components/icons/IconCalendar";
import IconBox from "@/components/icons/IconBox";
import { TransportType } from "@boxem/core-api-schema";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { productConditionToString } from "@/app/fba/utils";
import { formatValue } from "@/utils/formatUtils";
import ShipmentStatusBadge from "@/components/ShipmentStatusBadge/ShipmentStatusBadge";

type ExternalShipmentPageProps = {};

const ExternalShipmentPage = ({}: ExternalShipmentPageProps) => {
  const params = useParams<{ shipmentId: string}>();
  const { inventoryFlat } = useInventoryContext();
  const [shipmentLoading, setShipmentLoading] = useState<boolean>(false);
  const [shipment, setShipment] = useState<FbaShipment>({} as FbaShipment);
  const [location, setLocation] = useLocation();
  const [bolModalOpen, setBOLModalOpen] = useState<boolean>(false);
  const [bolLoading, setBolLoading] = useState<boolean>(false);
  const [productDetailsOpen, setProductDetailsOpen] = useState<boolean>(false);

  // const createdAt = new Date(shipment.createdAt).toLocaleString("default", { dateStyle: "medium", timeStyle: "medium" });
  const updatedAt = new Date(shipment.last_updated).toLocaleString("default", { dateStyle: "medium", timeStyle: "medium" });
  const totalUnits = () => {
    let units = 0;

    shipment?.items?.map(item => {
      units = units + item.quantity;
    });

    return units;
  };

  const handleGoBack = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    let prevTab = urlSearchParams.get("prev");
    setLocation(`/shipments?tab=${prevTab}`);
  };

  const handleViewBOL = async (shipmentId: number) => {
    setBolLoading(true);
    const bol = await getBillOfLadingV2(shipmentId).finally(() => setBolLoading(false));

    if (!!bol?.url) {
      const a = document.createElement('a');
      a.href = bol?.url;
      a.target = "_blank";
      a.download = "bol.pdf";
      a.click();
    } else {
      setBOLModalOpen(true);
    }
  };

  const getAddressValue = (address: FbaShipmentFromAddress): string => {
    let addressValue = "";

    if (!address?.address1) return addressValue;

    if (!!address?.address1) addressValue = addressValue + `${address?.address1}, `;
    if (!!address?.address2) addressValue = addressValue + `${address?.address2}, `;
    if (!!address?.city) addressValue = addressValue + `${address?.city} `;
    if (!!address?.state) addressValue = addressValue + `${address?.state} `;
    if (!!address?.zipcode) addressValue = addressValue + `${address?.zipcode}`;

    return addressValue;
  };

  useEffect(() => {
    if (inventoryFlat.length === 0 || !shipment?.items) {
      setShipmentLoading(true);
    }
    if (inventoryFlat.length && !shipment?.items) {
      getShipmentV2(params.shipmentId).then((result) => {
        const ship: FbaShipment = result;

        if (ship.items.length) {
          ship.items = ship.items.map((item) => {
            const inventoryItem = inventoryFlat.find(product => product.sellerData.find(data => data.sku === item.sku));
            const sellerData = inventoryItem?.sellerData.find(data => data.sku === item.sku);
  
            return {
              ...item,
              name: inventoryItem?.name ?? item.name,
              photo: inventoryItem?.image ?? item.image,
              asin: inventoryItem?.asin ?? item.asin,
              condition: sellerData?.condition ?? item.condition,
            }
          });
        }
        setShipment(ship);
      }).finally(() => {
        setShipmentLoading(false);
        setProductDetailsOpen(true);
      });
    }
  }, [inventoryFlat, shipment]);

  return (
    <div className={styles.pageContent}>
      <ConfirmModal
        title={"BOL Not Ready"}
        body={<p style={{margin:"20px", padding:"20px"}}>The bill of lading for this shipment is not ready yet - the BOL should be generated 1 day before the day of pick up.</p>} 
        open={!!bolModalOpen}
        onClose={(_: boolean): void => setBOLModalOpen(false)}
      />
      <div className={styles.topBarContainer}>
        <div className={styles.shipmentNameContainer}>
          <Button type="text" onClick={handleGoBack}>
            <IconArrowLeft size={32} />
            <div className={styles.shipmentSubTextContainer}>
              <div className={styles.shipmentName}>
                {shipmentLoading ? <div className={styles.shipmentNameLoader} /> : shipment.shipmentName}
              </div>
              <div className={styles.shipmentId}>
                {shipmentLoading ? <div className={styles.shipmentIdLoader} /> : <>Shipment ID: {shipment.shipmentId}</>}
              </div>
            </div>
          </Button>
        </div>
        <div className={styles.labelButtonsContainer}>
          {shipment.transportType === TransportType.LTL && (
            <Button
              type="primary"
              className={styles.buttonSpacing}
              onClick={() => handleViewBOL(shipment.id)}
            >
              Check for BOL
              <div className={styles.downloadIcon}>
                {bolLoading ? <LoadingSpinner className={styles.bolSpinner} size="1rem" /> : <IconDownloadBox />}
              </div>
            </Button>
          )}
        </div>
      </div>
      <div className={styles.externalShipmentNotice}>
        <IconInfoFilled />
        <div className={styles.noticeText}>EXTERNAL SHIPMENT: This shipment was created outside of Boxem.</div>
      </div>
      <div className={styles.shipmentDetailsContainer}>
        <div className={styles.productDetailsContainer}>
          <div className={styles.productDetailsHeaderContainer}>
            <div className={styles.productDetailsHeader}>
              <IconBox />
              <div className={styles.productDetailsHeaderText}>Products within shipment</div>
              <div className={styles.numOfShips}>
                {shipmentLoading ? <LoadingSpinner size={"1rem"} /> : <>({shipment?.items?.length ?? 0})</>}
              </div>
            </div>
            <Button
              type="text"
              disabled={shipmentLoading}
              onClick={() => setProductDetailsOpen(!productDetailsOpen)}
              className={`${styles.productDetailsControls} ${productDetailsOpen ? styles.productDetailsControlsOpen : ""}`}
            >
              <IconArrowDown />
            </Button>
          </div>
          <div className={`${styles.productDetailsItemsContainer} ${productDetailsOpen ? styles.productDetailsItemsContainerOpen : ""}`}>
            {!shipmentLoading && shipment?.items?.map((item, index) => {
              return (
                <div key={`${shipment.shipmentId}-${index}`} className={styles.productDetailsItemContainer}>
                  <div className={styles.productDetailsLeftColContainer}>
                    <div className={styles.productDetailsImageContainer}>
                      <img
                        className={styles.productDetailsImage}
                        src={item.photo}
                        alt={item.name}
                        style={{
                          gridColumn: 1,
                          gridRow: 1,
                          justifySelf: "center",
                          alignSelf: "start",
                          zIndex: 1,
                        }}
                      />
                    </div>
                    <div className={styles.shipmentItemQuantity}>x{item.quantity}</div>
                  </div>
                  <div className={styles.productDetailsRightColContainer}>
                    <div className="strong">{item.name}</div>
                    <div className="small-text weak">ASIN: <span className="small-text strong">{item.asin}</span></div>
                    <div className="small-text weak">SKU: <span className="small-text strong">{item.sku}</span></div>
                    <div className="small-text weak">Condition: <span className="small-text strong">{productConditionToString(item.condition)}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.shippingDetails}>
          <div className={styles.shipmentStatusContainer}>
            <div className={styles.iconContainer}>
              <IconClock size={16} />
            </div>
            <div className={styles.shipmentStatusTextContainer}>
              <div className={styles.shipmentStatusLabel}>Current Shipment Status</div>
              <div className={styles.shipmentStatusText}>
                {shipmentLoading ? <div className={styles.statusTextLoader} /> : <ShipmentStatusBadge status={shipment.status} />}
              </div>
            </div>
          </div>

          <div className={styles.shipmentHistoryContainer}>
            <div className={`${styles.shipmentHistoryLabel} strong`}>History</div>
            {/* <div className={`${styles.historySectionContainer} ${styles.historySectionContainerSpacing}`}>
              <div className={styles.iconContainer}>
                <IconCalendar size={16} />
                <div className={styles.historyTrack} />
              </div>
              <div className={styles.historyTextContainer}>
                <div className={styles.historyLabel}>Shipment created</div>
                <div className={styles.historyText}>
                  {shipmentLoading ? <div className={styles.statusTextLoader} /> : (
                    <>{createdAt}</>
                  )}
                </div>
              </div>
            </div> */}
            <div className={`${styles.historySectionContainer} ${styles.historySectionContainerSpacingSmall}`}>
              <div className={styles.iconContainer}>
                <IconCalendar size={16} />
              </div>
              <div className={styles.historyTextContainer}>
                <div className={styles.historyLabel}>Shipment last updated</div>
                <div className={styles.historyText}>
                  {shipmentLoading ? <div className={styles.statusTextLoader} /> : (
                    <>{updatedAt}</>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.shippingDetailsContainer}>
            <div className={`${styles.shippingDetailsLabel} strong`}>Shipping details</div>
            <div className={`${styles.shippingDetailsSectionContainer} ${styles.shippingDetailsSectionContainerSpacing}`}>
              <div className={styles.shippingIconContainer}>
                <img
                  className={styles.fromImage}
                  width={40}
                  height={40}
                  src={"/images/from.svg"}
                  alt="shipping from icon"
                  style={{
                    gridColumn: 1,
                    gridRow: 1,
                    justifySelf: "center",
                    alignSelf: "start",
                    zIndex: 1,
                  }}
                />
                <div className={styles.shippingDetailsTrack} />
              </div>
              <div className={styles.shippingDetailsTextContainer}>
                <div className="standard">Ship from</div>
                <div className="strong">
                  {shipmentLoading ? <div className={styles.shippingTextLoader} /> : (
                    <>{shipment?.from?.name ?? "n/a"}</>
                  )}
                </div>
                <div className="weak">
                  {shipmentLoading ? <div className={styles.shippingAddressLoader} /> : (
                    <>{getAddressValue(shipment.from)}</>
                  )}
                </div>
              </div>
            </div>
            <div className={`${styles.shippingDetailsSectionContainer} ${styles.shippingDetailsSectionContainerSpacingSmall}`}>
              <div className={styles.shippingIconContainer}>
                <img
                  width={40}
                  height={40}
                  src={"/images/to.svg"}
                  alt="shipping to icon"
                  style={{
                    gridColumn: 1,
                    gridRow: 3,
                    justifySelf: "center",
                    alignSelf: "start",
                    zIndex: 1,
                  }}
                />
              </div>
              <div className={styles.shippingDetailsTextContainer}>
                <div className="standard">Ship to</div>
                <div className="strong">
                  {shipmentLoading ? <div className={styles.shippingTextLoader} /> : (
                    <>Amazon {shipment?.fulfillmentCenterId} Warehouse</>
                  )}
                </div>
                <div className="weak">
                  {shipmentLoading ? <div className={styles.shippingAddressLoader} /> : (
                    <>{getAddressValue(shipment.destinationAddress)}</>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.shipmentContentsContainer}>
              <div className={styles.shipmentContentsLabel}>Shipment contents</div>
              <div className={styles.shippingContentContainer}>
                <div className={styles.shippingContentLabel}>Total number of SKUs</div>
                <div className={`${styles.shippingContentValue} strong`}>
                  {shipmentLoading ? <div className={styles.shippingContentValueLoader} /> : shipment?.items?.length}
                </div>
              </div>
              <div className={styles.shippingContentContainer}>
                <div className={styles.shippingContentLabel}>Total number of units</div>
                <div className={`${styles.shippingContentValue} strong`}>
                  {shipmentLoading ? <div className={styles.shippingContentValueLoader} /> : totalUnits()}
                </div>
              </div>
            </div>
            <div className={styles.shipmentContentsContainer}>
              <div className={`${styles.shippingContentContainer} ${styles.shippingContentValueSpacing}`}>
                <div className={styles.shippingContentLabel}>Estimated shipping charge</div>
                <div className={`${styles.estimatedShippingCharge} strong`}>
                  {shipmentLoading ? <div className={styles.shippingContentValueLoader} /> : <>{formatValue(shipment?.estimateAmount ?? 0.00, "currency")}&nbsp;{shipment.estimateCurrency}</>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalShipmentPage;